import { useEffect, useRef, useState } from "react";
import { client } from "../../amplify-client";

const SUBMIT_CONTACT_MESSAGE_MUTATION = /* GraphQL */ `
  mutation SubmitContactMessage($email: String!, $subject: String, $message: String!) {
    submitContactMessage(email: $email, subject: $subject, message: $message) {
      success
      message
      rateLimitRemaining
      rateLimitResetAt
    }
  }
`;

const EMPTY_CONTACT_FORM_DATA = {
  email: "",
  subject: "",
  message: "",
};

function clearSubmitStatusTimer(timeoutRef) {
  if (timeoutRef.current) {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
}

// Returns a fresh blank form state for the Contact screen.
export function getInitialContactFormData() {
  return { ...EMPTY_CONTACT_FORM_DATA };
}

// Validates whether an email value is well-formed enough for the contact form.
export function isValidContactEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(String(email || "").trim());
}

// Checks the required fields used to enable contact form submission.
export function isContactFormValid(formData) {
  return (
    String(formData?.email || "").trim() !== "" &&
    String(formData?.message || "").trim() !== "" &&
    isValidContactEmail(formData?.email)
  );
}

// Builds the contact message mutation payload from current form values.
export function buildContactMessagePayload(formData) {
  const payload = {
    email: String(formData?.email || "").trim(),
    message: String(formData?.message || "").trim(),
  };

  const subject = String(formData?.subject || "").trim();
  if (subject) {
    payload.subject = subject;
  }

  return payload;
}


async function submitContactMessageRequest(payload) {
  if (typeof client?.mutations?.submitContactMessage === "function") {
    return client.mutations.submitContactMessage(payload);
  }

  if (typeof client?.graphql === "function") {
    return client.graphql({
      query: SUBMIT_CONTACT_MESSAGE_MUTATION,
      variables: payload,
    });
  }

  throw new Error("Contact message mutation is unavailable. Deploy backend schema updates.");
}

function getContactSubmitErrorMessage(error) {
  const rawMessage = String(error?.message || "").trim();
  if (/too many contact messages|rate.?limit/i.test(rawMessage)) {
    return "Too many messages sent. Please wait before sending another message.";
  }
  return "Please try again in a moment.";
}

// Manages contact form state, validation, and message submission for the Contact screen.
export function useContactForm() {
  const [formData, setFormData] = useState(getInitialContactFormData);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const submitStatusTimeoutRef = useRef(null);

  useEffect(() => {
    return () => clearSubmitStatusTimer(submitStatusTimeoutRef);
  }, []);

  function handleInputChange(event) {
    const { name, value } = event.target;

    setFormData((previousFormData) => ({
      ...previousFormData,
      [name]: value,
    }));
  }

  function scheduleStatusReset() {
    clearSubmitStatusTimer(submitStatusTimeoutRef);

    submitStatusTimeoutRef.current = window.setTimeout(() => {
      setSubmitStatus(null);
      submitStatusTimeoutRef.current = null;
    }, 5000);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    clearSubmitStatusTimer(submitStatusTimeoutRef);

    try {
      const response = await submitContactMessageRequest(buildContactMessagePayload(formData));

      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      if (response?.data?.submitContactMessage?.success === false) {
        throw new Error(response.data.submitContactMessage.message || "Unable to send contact message.");
      }

      setSubmitStatus("success");
      setFormData(getInitialContactFormData());
      scheduleStatusReset();
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      setSubmitStatus(getContactSubmitErrorMessage(error));
      scheduleStatusReset();
    } finally {
      setSubmitting(false);
    }
  }

  return {
    formData,
    submitting,
    submitStatus,
    handleInputChange,
    handleSubmit,
    isFormValid: isContactFormValid(formData),
  };
}
